/**
 * Chrome shared by the cartesian charts: the series legend and the series data
 * table.
 *
 * Both the line and the bar chart plot many measures over one category axis, so
 * their legend and table are the same component with a different swatch. Only
 * the tooltip differs enough to live in each chart's own folder, because what it
 * reads out follows the mark: a crosshair down one x for lines, a single mark
 * plus its stack for bars.
 */

import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../ui/utils";
import { LegendPager } from "./LegendPager";
import type { CartesianPoint, ChartSeries } from "./cartesianUtils";

/* ------------------------------------------------------------------ */
/* Legend                                                              */
/* ------------------------------------------------------------------ */

/**
 * The swatch mirrors the mark it stands for: a rule for lines, a block for
 * bars, a dot or a diamond for scattered points depending on how they are
 * drawn. A legend showing a shape the plot does not contain is a key to a
 * different chart.
 */
export type LegendSwatch = "line" | "bar" | "dot" | "diamond";

interface SeriesLegendProps {
  series: ChartSeries[];
  activeId: string | null;
  hiddenIds: Set<string>;
  interactive: boolean;
  swatch?: LegendSwatch;
  onActivate: (id: string | null) => void;
  onToggle: (id: string) => void;
}

/**
 * Identity channel for the chart. Present whenever there is more than one
 * series, so meaning never rests on colour alone. Items are buttons: hover or
 * focus emphasises the matching mark, activation hides or restores it.
 */
export function SeriesLegend({
  series,
  activeId,
  hiddenIds,
  interactive,
  swatch = "line",
  onActivate,
  onToggle,
}: SeriesLegendProps) {
  return (
    // Left-aligned, not centred: the legend is a key read before the plot, so
    // it starts on the same edge as the card title and the y-axis below it.
    // One row that pages, rather than wrapping: extra lines would push the
    // plot down and leave two charts side by side misaligned.
    <LegendPager>
      <ul className="flex w-max flex-row flex-nowrap items-center gap-x-4">
      {series.map((entry) => {
        const hidden = hiddenIds.has(entry.id);
        const dimmed = !hidden && activeId != null && activeId !== entry.id;
        return (
          // `shrink-0` and a non-breaking label: on a single-line rail an item
          // that can compress or wrap defeats the paging it exists to enable.
          <li key={entry.id} className="shrink-0">
            <button
              type="button"
              disabled={!interactive}
              onMouseEnter={() => onActivate(entry.id)}
              onMouseLeave={() => onActivate(null)}
              onFocus={() => onActivate(entry.id)}
              onBlur={() => onActivate(null)}
              onClick={() => onToggle(entry.id)}
              aria-pressed={interactive ? !hidden : undefined}
              title={
                interactive
                  ? hidden
                    ? `Show ${entry.label}`
                    : `Hide ${entry.label}`
                  : undefined
              }
              className={cn(
                "flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left transition-opacity",
                interactive &&
                  "cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                !interactive && "cursor-default",
                hidden && "opacity-40",
                dimmed && "opacity-55",
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "shrink-0",
                  swatch === "bar" && "size-2.5 rounded-[3px]",
                  swatch === "dot" && "size-2.5 rounded-full",
                  swatch === "line" && "h-[3px] w-4 rounded-full",
                  // A square turned 45°. At 10px its diagonal is ~14px, which matches the scan
                  // timeline's 13px diamond, so the two legends read at the same weight.
                  swatch === "diamond" && "size-2.5 rotate-45 rounded-[2px]",
                  hidden && "ring-1 ring-inset ring-border",
                )}
                style={{
                  backgroundColor: hidden ? "transparent" : entry.color,
                  // The same tinted ring the slice and breakdown legends carry,
                  // so all three read as one control. Skipped on `line`: a 2px
                  // ring on a 3px rule doubles its weight into a fat bar.
                  boxShadow:
                    hidden || swatch === "line"
                      ? undefined
                      : `0 0 0 2px color-mix(in srgb, ${entry.color} 20%, transparent)`,
                }}
              />
              {/*
                Sized and coloured to match the scan timeline's legend, so two charts sitting in
                one answer read as the same control. The literal grays rather than
                `text-muted-foreground` are deliberate: that token is what made this smaller and
                fainter than the timeline's in the first place, and matching means matching.
              */}
              <span className="whitespace-nowrap text-sm font-medium leading-snug text-gray-700 dark:text-gray-300">
                {entry.label}
              </span>
            </button>
          </li>
        );
      })}
      </ul>
    </LegendPager>
  );
}

/* ------------------------------------------------------------------ */
/* Data table view                                                     */
/* ------------------------------------------------------------------ */

interface SeriesTableProps {
  points: CartesianPoint[];
  series: ChartSeries[];
  unit: string;
  xHeader: string;
  open: boolean;
  valueFormatter: (value: number) => string;
  onToggle: () => void;
}

/**
 * Text equivalent of the plot: one row per category, one column per series.
 *
 * This is the fallback for anyone who cannot read the mark colours, and the only
 * place a reader can get exact values for every point without hovering. Missing
 * values render as a dash so they are never mistaken for zero.
 */
export function SeriesTable({
  points,
  series,
  unit,
  xHeader,
  open,
  valueFormatter,
  onToggle,
}: SeriesTableProps) {
  return (
    <div className="mt-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-md px-1 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        {open ? "Hide data table" : "Show data table"}
      </button>

      {open && (
        // Many series can exceed the card width, so this one table is allowed
        // to scroll sideways inside its own box.
        <div className="mt-2 max-h-[280px] overflow-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b border-border">
                <th scope="col" className="py-1.5 pr-3 font-semibold text-muted-foreground">
                  {xHeader}
                </th>
                {series.map((entry) => (
                  <th
                    key={entry.id}
                    scope="col"
                    className="py-1.5 pl-3 text-right font-semibold text-muted-foreground"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        aria-hidden="true"
                        className="h-[3px] w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: entry.color }}
                      />
                      {entry.label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {points.map((point, index) => (
                <tr key={`${point.x}-${index}`} className="border-b border-border/50 last:border-0">
                  <th scope="row" className="py-1.5 pr-3 font-medium text-foreground">
                    <span className="break-words">{point.x}</span>
                  </th>
                  {series.map((entry) => {
                    const value = point[entry.key];
                    return (
                      <td
                        key={entry.id}
                        className="py-1.5 pl-3 text-right font-bold tabular-nums text-foreground"
                      >
                        {typeof value === "number" ? (
                          <>
                            {valueFormatter(value)}
                            {unit}
                          </>
                        ) : (
                          <span className="font-normal text-muted-foreground">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
